import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import { DonutChartSegment } from '@custom-components/general/donut-chart/donut-chart.component';
import { APP_PATHS, CHART_SERIES, CHART_SERIES_NEUTRAL } from '@core/constants';
import { canViewInstallations } from '@core/helpers/installation-access.helper';
import { InstallationFilter } from '@core/models/installation.model';
import { DeviceService, InstallationService, LocationService } from '@core/services';
import { AlarmsOverviewService } from '@core/services/alarms-overview.service';
import { AlarmTypeCatalogService } from '@core/services/alarm-type-catalog.service';
import { RecentlyVisitedService } from '@core/services/recently-visited.service';
import { AuthStore } from '@root/app/store/auth.store';
import { UserStore } from '@root/app/store/user.store';
import {
  AlarmOverviewKpisModel,
  AlarmOverviewRowModel,
  ConnectionStateEnum,
  DeviceFiltersModel,
  DeviceModel,
  InstallationStateEnum,
  LocationPayloadModel,
  capitalizePriority,
} from '@core/models';
import {
  ALARM_PRIORITY_ACCENT_COLOR,
  TagStateOf,
  alarmConfigGroupLabelKey,
  configGroupForAlarmType,
  displayAlarmTypeName,
} from '@core/helpers';
import {
  AlarmPriority,
  AlarmPriorityWedge,
  AlarmTypeBar,
  DashboardAlarmsWidgetComponent,
  DashboardDevicesWidgetComponent,
  DashboardKpiTilesComponent,
  DashboardKpis,
  DashboardNewAlarmsWidgetComponent,
  DashboardRecentlyUsedWidgetComponent,
  NewAlarmRow,
  RecentlyVisitedRow,
} from '../../components';

const SEGMENT_COLORS = {
  positive: CHART_SERIES[0],
  negative: ALARM_PRIORITY_ACCENT_COLOR['High'],
  warning: ALARM_PRIORITY_ACCENT_COLOR['Medium'],
  neutral: CHART_SERIES_NEUTRAL,
} as const;

/** Config groups always shown in the by-type breakdown, even at 0 — same set the settings tab
 * always lists regardless of whether any alarm of that type is currently open. Any alarm_type
 * that maps to none of these (e.g. a cloud-only type outside the alarm-config catalog) still
 * gets its own bar, appended after this fixed set — so a new type still auto-appears (RDT-718),
 * it just isn't one of the "always visible" baseline categories.
 *
 * Must stay in step with the backend's `alarm_config_group_catalog.ALARM_CONFIG_GROUP_CATALOG`
 * (xmx-dpf-alarm): a group missing here is silently absent from the donut, the by-type bars *and*
 * the open-alarms KPI tile, since all three are scoped to this set. That is exactly how the SIM
 * groups went unrepresented on the homepage after they were added backend-side. */
const BASE_ALARM_CONFIG_GROUPS = [
  'device_connection',
  'no_data_alarm',
  'dlms.standard',
  'dlms.fraud',
  'dlms.power_quality',
  'dlms.communication',
  'sim.data_depleted',
  'sim.data_depleted_high',
  'sim.end_date_approaching',
  'sim.end_date_approaching_high',
  'production_stopped',
  'pv_underperformance',
];

@Component({
  selector: 'xemex-dashboard-page',
  imports: [
    PageHeaderComponent,
    PageBodyComponent,
    DashboardKpiTilesComponent,
    DashboardDevicesWidgetComponent,
    DashboardAlarmsWidgetComponent,
    DashboardNewAlarmsWidgetComponent,
    DashboardRecentlyUsedWidgetComponent,
    TranslateModule,
  ],
  providers: [TranslatePipe],
  templateUrl: './dashboard-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPageComponent {
  private readonly translateService = inject(TranslatePipe);
  private readonly deviceService = inject(DeviceService);
  private readonly locationService = inject(LocationService);
  private readonly installationService = inject(InstallationService);
  private readonly authStore = inject(AuthStore);
  private readonly alarmsOverviewService = inject(AlarmsOverviewService);
  private readonly alarmTypeCatalog = inject(AlarmTypeCatalogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  protected readonly recentlyVisited = inject(RecentlyVisitedService);
  protected readonly userStore = inject(UserStore);

  protected readonly APP_PATHS = APP_PATHS;
  protected readonly loading = signal(true);

  private readonly totalLocations = signal(0);
  private readonly totalDevices = signal(0);
  private readonly showInstallations = computed(() =>
    canViewInstallations(this.userStore.user(), this.authStore.permissions()),
  );
  // Null until counted, and never counted for a caller who cannot see installations, so the
  // tile is left out rather than showing a zero.
  private readonly totalInstallations = signal<number | null>(null);
  private readonly incompleteInstallations = signal<number | null>(null);
  private installationsRequested = false;
  // Device counts per status combination (RDT-1565). Null until counted, or when the count
  // failed: that segment is left out and the rest of the widget renders.
  private readonly onlineInstalled = signal<number | null>(null);
  private readonly offlineInstalled = signal<number | null>(null);
  private readonly toBeInstalledDevices = signal<number | null>(null);
  private readonly installationFailedDevices = signal<number | null>(null);
  private readonly apiKpis = signal<AlarmOverviewKpisModel | null>(null);
  private readonly alarmRows = signal<AlarmOverviewRowModel[]>([]);

  constructor() {
    effect(() => {
      if (this.installationsRequested || !this.showInstallations()) return;
      this.installationsRequested = true;
      this.countInstallations().subscribe(count => this.totalInstallations.set(count));
      this.countInstallations({ commissioning: 'incomplete' }).subscribe(count =>
        this.incompleteInstallations.set(count),
      );
    });

    forkJoin({
      locations: this.locationService
        .getListLocationsFilter({ limit: 1, offset: 0 } as LocationPayloadModel)
        .pipe(catchError(() => of(null))),
      devices: this.deviceService
        .getListDevicesFilter({ limit: 1, offset: 0 } as DeviceFiltersModel)
        .pipe(catchError(() => of(null))),
      onlineInstalled: this.countDevices({
        connectionState: ConnectionStateEnum.Online,
        installationState: InstallationStateEnum.Installed,
      }),
      offlineInstalled: this.countDevices({
        connectionState: ConnectionStateEnum.Offline,
        installationState: InstallationStateEnum.Installed,
      }),
      toBeInstalled: this.countDevices({ installationState: InstallationStateEnum.ToBeInstalled }),
      installationFailed: this.countDevices({
        installationState: InstallationStateEnum.InstallationFailed,
      }),
      alarms: this.alarmsOverviewService.getOverview().pipe(catchError(() => of(null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ({
          locations,
          devices,
          onlineInstalled,
          offlineInstalled,
          toBeInstalled,
          installationFailed,
          alarms,
        }) => {
          this.loading.set(false);
          if (locations) this.totalLocations.set(locations.numberOfItems);
          if (devices) this.totalDevices.set(devices.numberOfItems);
          this.onlineInstalled.set(onlineInstalled);
          this.offlineInstalled.set(offlineInstalled);
          this.toBeInstalledDevices.set(toBeInstalled);
          this.installationFailedDevices.set(installationFailed);
          if (alarms) {
            this.apiKpis.set(alarms.kpis);
            this.alarmRows.set(alarms.items);
          }
        },
      );
  }

  /** Count for one state segment. The sample device must carry the requested state(s): a missing
   * or different value means the backend ignored the filter (state fields off for this partner,
   * D473, or an old backend) and its total would land in every segment, so the segment is left
   * out instead, like a failed count. */
  private countDevices(filter: Partial<DeviceFiltersModel>): Observable<number | null> {
    return this.deviceService
      .getListDevicesFilter({ limit: 1, offset: 0, ...filter } as DeviceFiltersModel)
      .pipe(
        map(response => {
          const total = response?.numberOfItems ?? null;
          const sample = response?.devices?.[0];
          if (sample) return this.matchesFilter(sample, filter) ? total : null;
          // Nothing to verify the filter on: only an empty result is a trustworthy zero.
          return total === 0 ? 0 : null;
        }),
        catchError(() => of(null)),
      );
  }

  private matchesFilter(device: DeviceModel, filter: Partial<DeviceFiltersModel>): boolean {
    const { installationState, connectionState } = filter;
    return (
      (!installationState || device.installationState === installationState) &&
      (!connectionState || device.connectionState === connectionState)
    );
  }

  private countInstallations(filter: Partial<InstallationFilter> = {}): Observable<number | null> {
    return this.installationService.list({ limit: 1, offset: 0, ...filter }).pipe(
      map(response => response?.numberOfItems ?? null),
      catchError(() => of(null)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  protected readonly kpis = computed<DashboardKpis>(() => ({
    totalInstallations: this.showInstallations() ? this.totalInstallations() : null,
    incompleteInstallations: this.showInstallations() ? this.incompleteInstallations() : null,
    totalLocations: this.totalLocations(),
    totalDevices: this.totalDevices(),
    openAlarms: this.visibleAlarmsTotal(),
    alarmsLast7Days: this.apiKpis()?.opened_last_7_days ?? 0,
  }));

  protected readonly installedDevices = computed(() => {
    const online = this.onlineInstalled();
    const offline = this.offlineInstalled();
    return online !== null && offline !== null ? online + offline : null;
  });

  /** Online devices in the centre of the devices donut (D499); a dash when none are installed. */
  protected readonly devicesCenterLabel = computed(() => {
    const installed = this.installedDevices();
    return installed ? String(this.onlineInstalled() ?? 0) : '-';
  });

  /** Online, Offline, Registration failed, Not yet registered: together all visible devices. */
  protected readonly deviceSegments = computed(() =>
    this.segments([
      { label: 'Online', value: this.onlineInstalled(), color: SEGMENT_COLORS.positive },
      { label: 'Offline', value: this.offlineInstalled(), color: SEGMENT_COLORS.negative },
      {
        label: InstallationStateEnum.InstallationFailed,
        value: this.installationFailedDevices(),
        color: SEGMENT_COLORS.warning,
      },
      {
        label: InstallationStateEnum.ToBeInstalled,
        value: this.toBeInstalledDevices(),
        color: SEGMENT_COLORS.neutral,
      },
    ]),
  );

  private segments(
    candidates: { label: string; value: number | null; color: string }[],
  ): DonutChartSegment[] {
    return candidates.flatMap(({ label, value, color }) =>
      value === null ? [] : [{ label, value, color }],
    );
  }

  /** Opens the devices list on the installed devices that are online or offline. */
  protected onConnectionFilterClick(status: string): void {
    void this.router.navigate(['/', APP_PATHS.Devices], {
      queryParams: { status, installationState: InstallationStateEnum.Installed },
    });
  }

  /** Opens the devices list on one installation state. */
  protected onDeviceSegmentClick(label: string): void {
    if (label === 'Online' || label === 'Offline') this.onConnectionFilterClick(label);
    else this.onInstallationFilterClick(label);
  }

  protected onInstallationFilterClick(installationState: string): void {
    void this.router.navigate(['/', APP_PATHS.Devices], { queryParams: { installationState } });
  }

  /** Scoped to the same config-group categories as `alarmsByType` (not every open alarm) so
   * this donut/legend always sums to exactly the same total as the by-type bars below it —
   * see `alarmsByType` for why those bars are capped to the baseline set. */
  private readonly alarmsByPriority = computed(() => {
    const counts: Record<AlarmPriority, number> = { High: 0, Medium: 0, Low: 0 };
    for (const row of this.alarmRows()) {
      if (BASE_ALARM_CONFIG_GROUPS.includes(configGroupForAlarmType(row.alarm_type))) {
        counts[capitalizePriority(row.priority)]++;
      }
    }
    return counts;
  });

  protected readonly visibleAlarmsTotal = computed(() => {
    const counts = this.alarmsByPriority();
    return counts.High + counts.Medium + counts.Low;
  });

  /** Solid-fill pie, one clickable/hoverable segment per priority, rendered by the widget's own
   * Chart.js `pie` canvas. Each wedge navigates to the Alarms overview page pre-filtered to that
   * priority. */
  protected readonly priorityWedges = computed<AlarmPriorityWedge[]>(() => {
    const counts = this.alarmsByPriority();
    return (['High', 'Medium', 'Low'] as const)
      .filter(priority => counts[priority] > 0)
      .map(priority => ({
        priority,
        color: ALARM_PRIORITY_ACCENT_COLOR[priority],
        label: this.translateService.transform(
          `home.alarmsWidget.priority.${priority.toLowerCase()}`,
        ),
        count: counts[priority],
      }));
  });

  protected onPriorityFilterClick(priority: AlarmPriority): void {
    this.router.navigate(['/', APP_PATHS.Alarms], { queryParams: { priority } });
  }

  protected onAlarmTypeFilterClick(typeLabel: string): void {
    this.router.navigate(['/', APP_PATHS.Alarms], { queryParams: { type: typeLabel } });
  }

  /** Strictly the same config-group categories the alarm-settings tab always shows — nothing
   * else. Per Laurens (2026-07-24): keep this to exactly BASE_ALARM_CONFIG_GROUPS rather than also
   * surfacing types outside that set as extra bars; every alarm is expected to fall under one
   * of them, so these bars can legitimately undercount the total open-alarm KPI.
   *
   * Per-type bars are split into High/Medium/Low segments (same colors as the priority donut
   * above) so the composition of each type's alarms is visible, not just its total count. */
  protected readonly alarmsByType = computed<AlarmTypeBar[]>(() => {
    const byPriority = new Map<string, Record<AlarmPriority, number>>(
      BASE_ALARM_CONFIG_GROUPS.map(group => [group, { High: 0, Medium: 0, Low: 0 }]),
    );

    for (const row of this.alarmRows()) {
      const priorities = byPriority.get(configGroupForAlarmType(row.alarm_type));
      if (priorities) priorities[capitalizePriority(row.priority)]++;
    }

    return BASE_ALARM_CONFIG_GROUPS.map(group => {
      const priorities = byPriority.get(group) ?? { High: 0, Medium: 0, Low: 0 };
      const count = priorities.High + priorities.Medium + priorities.Low;
      return {
        group,
        label: this.translateService.transform(alarmConfigGroupLabelKey(group)),
        count,
        segments: (['High', 'Medium', 'Low'] as const)
          .filter(priority => priorities[priority] > 0)
          .map(priority => ({
            priority,
            count: priorities[priority],
            color: ALARM_PRIORITY_ACCENT_COLOR[priority],
          })),
      };
    });
  });

  /** Widest bar's share of the row — `1` (never 0) so a fully-empty set still renders zero-width
   * bars instead of dividing by zero. */
  protected readonly alarmTypeMaxCount = computed(() =>
    Math.max(1, ...this.alarmsByType().map(t => t.count)),
  );

  protected readonly newAlarms = computed<NewAlarmRow[]>(() => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return [...this.alarmRows()]
      .filter(row => new Date(row.opened_at).getTime() >= sevenDaysAgo)
      .sort((a, b) => new Date(b.opened_at).getTime() - new Date(a.opened_at).getTime())
      .slice(0, 10)
      .map(row => {
        const priority = capitalizePriority(row.priority);
        return {
          id: row.id,
          deviceId: row.device_id,
          deviceLdn: row.device_ldn,
          typeLabel: displayAlarmTypeName(
            row.alarm_type,
            k => this.alarmTypeCatalog.label(k),
            k => this.translateService.transform(k),
          ),
          priorityLabel: this.translateService.transform(
            `home.alarmsWidget.priority.${priority.toLowerCase()}`,
          ),
          priorityState: TagStateOf.alarmPriority(priority),
          age: this.formatAlarmAge(row.opened_at),
        };
      });
  });

  // Same format as the Alarms page's overview table (AlarmsOverviewTabComponent.formatAge) —
  // kept in sync deliberately rather than shared, since this widget only needs the format.
  private formatAlarmAge(openedAt: string): string {
    const minutesAgo = Math.max(
      0,
      Math.round((Date.now() - new Date(openedAt).getTime()) / 60_000),
    );
    if (minutesAgo < 60) return `${minutesAgo} min`;
    const hours = Math.floor(minutesAgo / 60);
    if (hours < 24) return `${hours} hrs`;
    return `${Math.floor(hours / 24)} days`;
  }

  protected onNewAlarmClick(deviceId: string): void {
    this.router.navigate(['/', APP_PATHS.Devices, deviceId, 'alarms']);
  }

  // The list is per browser rather than per user, so an installation a Xemex user opened would
  // otherwise show up for the next user who signs in on the same browser.
  protected readonly recentlyVisitedRows = computed<RecentlyVisitedRow[]>(() => {
    const showInstallations = this.showInstallations();
    return this.recentlyVisited
      .recent()
      .filter(entry => showInstallations || entry.type !== 'installation')
      .map(entry => ({
        ...entry,
        timeAgo: this.timeAgoFor(entry.visitedAt),
      }));
  });

  private timeAgoFor(visitedAt: number): string {
    const minutes = Math.max(0, Math.round((Date.now() - visitedAt) / 60_000));
    if (minutes < 1) return this.translateService.transform('recentlyUsed.justNow');
    if (minutes < 60) {
      return this.translateService.transform('recentlyUsed.minutesAgo', { value: minutes });
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return this.translateService.transform('recentlyUsed.hoursAgo', { value: hours });
    }
    return this.translateService.transform('recentlyUsed.daysAgo', {
      value: Math.floor(hours / 24),
    });
  }

  protected navigateTo(route: string[]): void {
    this.router.navigate(route);
  }
}
